/**
 * Same-origin fetch wrapper for the BFF.
 *
 * The browser sends the HttpOnly session cookie automatically because
 * the SPA and the Spring backend share the same origin (in dev via the
 * Vite proxy, in prod via nginx + the BFF container). Mutating verbs
 * carry the double-submit CSRF token that [csrf.ts] keeps in memory.
 *
 * 401 / 403 are surfaced as ApiError so the caller can decide what to
 * do — the route guards in src/router.tsx fan them out to either a
 * /auth/login navigation (when the failure is on a protected route)
 * or a silent "not signed in yet" branch (when it comes from the
 * /auth/me probe).
 */
import { getCsrfTokenCached, refreshCsrfToken } from "@/auth/csrf";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown,
  ) {
    super(`HTTP ${status} ${statusText}`);
  }
}

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();

  const headers = new Headers(init.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (MUTATING_METHODS.has(method)) {
    const csrf = await getCsrfTokenCached();
    if (csrf) headers.set("X-XSRF-TOKEN", csrf);
  }

  const response = await fetch(path, {
    ...init,
    method,
    credentials: "include",
    headers,
  });

  if (response.status === 403 && MUTATING_METHODS.has(method)) {
    await refreshCsrfToken();
  }

  if (!response.ok) {
    const body = await safelyParseBody(response);
    throw new ApiError(response.status, response.statusText, body);
  }

  if (response.status === 204) return undefined as unknown as T;
  return (await response.json()) as T;
}

async function safelyParseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}
